import React, { useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';

export interface ModalActionFooterProps {
  onSave?: () => void;
  saveLabel?: string;
  saveDisabled?: boolean;
  onCancel?: () => void;
  cancelLabel?: string;
  onDelete?: () => void;
  deleteLabel?: string;
  deleteConfirmMessage?: string;
}

const ModalActionFooter: React.FC<ModalActionFooterProps> = ({
  onSave,
  saveLabel = 'Save',
  saveDisabled = false,
  onCancel,
  cancelLabel = 'Cancel',
  onDelete,
  deleteLabel = 'Delete',
  deleteConfirmMessage = 'Are you sure you want to delete this item? This action cannot be undone.',
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleDeleteClick = () => setConfirmOpen(true);
  const handleConfirmClose = () => setConfirmOpen(false);
  const handleConfirmDelete = () => {
    setConfirmOpen(false);
    onDelete?.();
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: 'row',
          gap: 1,
          width: '100%',
        }}
      >
        {/* Left side: Delete */}
        <Box>
          {onDelete && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
            >
              {deleteLabel}
            </Button>
          )}
        </Box>

        {/* Right side: Cancel + Save */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onCancel && (
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={onCancel}
            >
              {cancelLabel}
            </Button>
          )}
          {onSave && (
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={onSave}
              disabled={saveDisabled}
            >
              {saveLabel}
            </Button>
          )}
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={handleConfirmClose} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>{deleteConfirmMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose}>{cancelLabel}</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModalActionFooter;
